"use client";

import React, {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import Cookie from "js-cookie";

import { DEFAULT_LOCALE, LOCALES } from "~/constants/i18n";
import { DY_ID, DY_ID_SERVER } from "~/constants/user-cookies";
import { formatLocaleString } from "~/helpers/locales/format-locale-string";
import { LocaleFormat } from "~/helpers/locales/types";
import { setDYContext } from "~/helpers/set-dy-context";
import { AzureConfigurator } from "~/services/azure-configurator/azure-configurator";
import { chooseCampaignVariations } from "~/utils/dynamic-yield/choose-campaign-variations";
import {
  DYChooseResponseData,
  ChooseCampaignVariationsArgs,
  DYContextType,
  DyCampaignName,
} from "~/utils/dynamic-yield/types";

import { DynamicYieldContext } from "./context";
import { useDyConsent } from "./hooks/use-dy-consent";
import { DynamicYieldContextValue } from "./types";

export type DynamicYieldProviderProps = PropsWithChildren<{
  pageLocale: LOCALES;
  pageData?: string[];
  pageType: DYContextType;
  pageLocation: string;
  previewIds?: string | string[];
  campaignNames: DyCampaignName[];
}>;

export const DynamicYieldProvider: FC<DynamicYieldProviderProps> = ({
  children,
  pageLocale,
  pageData,
  pageType,
  pageLocation,
  previewIds,
  campaignNames,
}) => {
  const [dyChooseResponseData, setDyChooseResponseData] =
    useState<DYChooseResponseData | null>(null);

  const isDynamicYieldEnabled =
    AzureConfigurator.getConfig(pageLocale)?.featureFlagList?.dynamicYield?.enabled;

  const { isDyConsentAccepted, isCookieBannerClosed } = useDyConsent();

  const dyPageLocale = formatLocaleString(
    pageLocale,
    LocaleFormat.xx_dash_xx,
    LocaleFormat.xx_XX,
  );

  const chooseCampaignVariationsArgs = useMemo<ChooseCampaignVariationsArgs>(
    () => ({
      pageType,
      pageData,
      pageLocation,
      pageLocale: dyPageLocale ?? DEFAULT_LOCALE,
      campaignNames,
      previewIds: previewIds ?? [],
    }),
    [pageType, pageData, pageLocation, dyPageLocale, campaignNames, previewIds],
  );

  const dyContextInfo = {
    locale: dyPageLocale ?? DEFAULT_LOCALE,
    data: pageData,
    type: pageType,
  };

  const hasDoneInitialCallRef = React.useRef(false);
  const hasHandledFirstConsentRef = React.useRef(false);

  const performChoose = useCallback(async () => {
    let dyIdCookieValue: string | undefined;

    if (isDyConsentAccepted) {
      let retries = 0;
      dyIdCookieValue = Cookie.get(DY_ID);
      while (!dyIdCookieValue && retries < 10) {
        await new Promise((resolve) => setTimeout(resolve, 200));
        retries++;
        dyIdCookieValue = Cookie.get(DY_ID);
      }
    }

    const dyIdServerCookieValue = Cookie.get(DY_ID_SERVER);

    if (dyIdCookieValue && !dyIdServerCookieValue) {
      await fetch("/api/dy/consent-cookie", { method: "GET" });
    }

    if (!hasDoneInitialCallRef.current) {
      hasDoneInitialCallRef.current = true;
    } else if (!hasHandledFirstConsentRef.current) {
      hasHandledFirstConsentRef.current = true;

      if (!isDyConsentAccepted) {
        return;
      }
    }

    const dyChooseResponseDataLocal = await chooseCampaignVariations(
      chooseCampaignVariationsArgs,
    );

    setDyChooseResponseData(dyChooseResponseDataLocal);
  }, [chooseCampaignVariationsArgs, isDyConsentAccepted]);

  useEffect(() => {
    if (
      isDynamicYieldEnabled &&
      chooseCampaignVariationsArgs.campaignNames.length > 0
    ) {
      setDYContext(
        dyContextInfo.type,
        dyContextInfo.locale,
        dyContextInfo.data ?? null,
      );

      performChoose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDyConsentAccepted, isCookieBannerClosed]);

  const contextValue: DynamicYieldContextValue = useMemo(
    () => ({
      chooseCampaignVariationsArgs,
      chooseResponseData: dyChooseResponseData,
    }),
    [chooseCampaignVariationsArgs, dyChooseResponseData],
  );

  return (
    <DynamicYieldContext.Provider value={contextValue}>
      {children}
    </DynamicYieldContext.Provider>
  );
};
